import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { updateCampaignType, getCampaignTypeById } from "../Services/APIService";
import { CampaignTypeFormData, CampaignType } from "../types/campaignType.types";


interface ValidationErrors {
  name?: string;
}

interface UseEditCampaignTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CampaignTypeFormData) => void; // Callback to notify parent for refresh
  campaignTypeId: number | null;
}

export const useEditCampaignTypeModal = ({
  isOpen,
  onClose,
  onSubmit,
  campaignTypeId,
}: UseEditCampaignTypeModalProps) => {
  const [formData, setFormData] = useState<CampaignTypeFormData>({
    id: 0,
    name: "",
    isactive: true
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem("authToken") || "";
  const tenantId = Number(localStorage.getItem("tenantId")) || 1000;

  useEffect(() => {
    if (isOpen && campaignTypeId) {
      const fetchCampaignType = async () => {
        setIsLoading(true);
        try {
          if (!campaignTypeId || campaignTypeId <= 0) {
            throw new Error("Invalid campaign type ID");
          }
          if (!tenantId || tenantId <= 0) {
            throw new Error("Invalid tenant ID");
          }
          if (!token) {
            throw new Error("Missing authentication token");
          }

          console.log("Fetching campaign type with:", { campaignTypeId, tenantId });
          const response = await getCampaignTypeById(campaignTypeId);
          console.log("Fetching campaign Data:", { response });

          // Handle direct CampaignType response
          const campaignType = response as unknown as CampaignType;
          if (campaignType?.id && campaignType.name !== undefined) {
            setFormData({
              id: campaignType.id,
              name: campaignType.name,
              isactive: campaignType.isActive,
            
            });
            setErrors({});
          } else {
            throw new Error("Invalid campaign type data returned");
          }
        } catch (error: any) {
          console.error("useEditCampaignTypeModal fetch error:", error, {
            campaignTypeId,
            tenantId,
            response: error.message.includes("Invalid JSON") ? "Invalid JSON response" : error.message,
          });
          toast.error(
            error.message.includes("Invalid JSON")
              ? "Server returned invalid data. Please try again or contact support."
              : error.message.includes("not found")
              ? "Campaign type not found. It may have been deleted."
              : error.message || "Failed to load campaign type"
          );
          onClose();
        } finally {
          setIsLoading(false);
        }
      };
      fetchCampaignType();
    }
  }, [isOpen, campaignTypeId, tenantId, token]);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = "Campaign type name is required";
      isValid = false;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Campaign type name must be at least 2 characters";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleClearAll = () => {
    setFormData({
      id: 0,
      name: "",
      isactive: true,
     
    });
    setErrors({});
    onClose();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const processedValue = type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

    setFormData((prev) => ({ ...prev, [name]: processedValue }));
    if (errors?.name) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      //toast.error("Please fix the errors in the form");
      return;
    }

    setIsLoading(true);
    try {
      if (!token) {
        throw new Error("Missing authentication token");
      }
      const trimmedData = {
        ...formData,
        name: formData.name.trim().replace(/\s+/g, " "),
      };
      console.log("Submitting update with:", trimmedData);
      const response = await updateCampaignType(trimmedData);
      console.log("updateCampaignType response:", response);

      if (response?.isUpdated) {
       
        onSubmit(trimmedData);
        onClose();
      } else if (response?.message?.toLowerCase().includes("already exists")) {
        setErrors({ name: "Campaign type name already exists" });
        toast.error("Campaign type name already exists");
      } else {
        throw new Error(response?.message || "Campaign type update failed");
      }
    } catch (error: any) {
      
      if (error.message.toLowerCase().includes("already exists")) {
        setErrors({ name: "Campaign type name already exists" });
        toast.error("Campaign type name already exists");
      } else {
        toast.error(error.message || "Failed to update campaign type");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formData,
    errors,
    isLoading,
    handleChange,
    handleClearAll,
    handleSubmit,
  };
};